import React, { useState } from 'react';
import {
  Utensils,
  Car,
  Zap,
  Gamepad2,
  ShoppingBag,
  Heart,
  GraduationCap,
  PiggyBank,
  MoreHorizontal,
  Briefcase,
  Laptop,
  TrendingUp,
  DollarSign,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useBudget, CATEGORIES, INCOME_CATEGORIES } from '@/contexts/BudgetContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const iconMap = {
  Utensils,
  Car,
  Zap,
  Gamepad2,
  ShoppingBag,
  Heart,
  GraduationCap,
  PiggyBank,
  MoreHorizontal,
  Briefcase,
  Laptop,
  TrendingUp,
  DollarSign,
};

const formatDate = (dateString) => {
  // Parse YYYY-MM-DD format properly to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const TransactionItem = ({ transaction, onDelete, formatCurrency }) => {
  const { type, category, amount, description, date, id } = transaction;
  const isIncome = type === 'income';
  const categories = isIncome ? INCOME_CATEGORIES : CATEGORIES;
  const categoryConfig = categories[category];
  const IconComponent = iconMap[categoryConfig?.icon] || DollarSign;

  return (
    <div className="flex items-center gap-3 py-3 group animate-fade-in">
      <div
        className={cn(
          'w-11 h-11 rounded-xl flex items-center justify-center shrink-0',
          isIncome ? 'bg-success/15' : 'bg-muted'
        )}
        style={
          !isIncome
            ? {
                backgroundColor: `hsl(var(--${categoryConfig?.color}) / 0.15)`,
              }
            : undefined
        }
      >
        <IconComponent
          className="w-5 h-5"
          style={{
            color: isIncome
              ? 'hsl(var(--success))'
              : `hsl(var(--${categoryConfig?.color}))`,
          }}
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {description || category}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground">{category}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">{formatDate(date)}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span
          className={cn(
            'text-sm font-semibold tabular-nums',
            isIncome ? 'text-success' : 'text-foreground'
          )}
        >
          {isIncome ? '+' : '-'}
          {formatCurrency(amount)}
        </span>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-w-[90vw] sm:max-w-[400px] rounded-xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Transaction</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this transaction? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(id)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export const TransactionList = () => {
  const { getMonthTransactions, deleteTransaction, currentMonth, setCurrentMonth, formatCurrency } =
    useBudget();
  const [filter, setFilter] = useState('all');

  const transactions = getMonthTransactions();

  const filteredTransactions = transactions
    .filter((t) => {
      if (filter === 'all') return true;
      if (filter === 'income') return t.type === 'income';
      if (filter === 'expense') return t.type === 'expense';
      return t.category === filter;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const handleDelete = (id) => {
    deleteTransaction(id);
    toast.success('Transaction deleted');
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      1
    );
    if (nextMonth <= new Date()) {
      setCurrentMonth(nextMonth);
    }
  };

  const monthName = currentMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const isCurrentMonth =
    currentMonth.getMonth() === new Date().getMonth() &&
    currentMonth.getFullYear() === new Date().getFullYear();

  return (
    <div className="space-y-4">
      {/* Month Navigation */}
      <Card className="border border-border/50 shadow-card">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={handlePrevMonth}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-sm font-semibold text-foreground">
              {monthName}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={handleNextMonth}
              disabled={isCurrentMonth}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-[140px]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="expense">Expenses</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">
          {filteredTransactions.length} transactions
        </span>
      </div>

      {/* Transaction List */}
      <Card className="border border-border/50 shadow-card">
        <CardContent className="p-2">
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                No transactions found
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {filter !== 'all'
                  ? 'Try changing the filter'
                  : 'Add your first transaction'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {filteredTransactions.map((transaction) => (
                <TransactionItem
                  key={transaction.id}
                  transaction={transaction}
                  onDelete={handleDelete}
                  formatCurrency={formatCurrency}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
